import SectionCard from './SectionCard'
import { isLoanScenario } from './scenarioType'
import FieldRow from './FieldRow'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import MonthlyTotalToggle from './MonthlyTotalToggle'
import { formatMoney } from './format'

// Verbatim from files/sow3/spec/holding-costs-header-spec.28.09.md 1.5 (node 6851:2141). Utilities
// and HOA Fees carry no icon in the design.
// Accrued Interest follows Cong's text in letter 115.
const TOOLTIPS = {
  accruedInterest:
    'Estimated interest over the project timeline, based on your loan terms and holding period.',
  drawFees:
    'Estimated total fees charged by the lender for releasing rehab funds through draws. Enter the combined cost of all expected draw fees.\n\nCheck the lender’s term sheet for the fee per draw and expected number of draws.',
}

// Monthly: editable monthly amount | divider | total over the holding period. Total: the monthly
// box goes and the total becomes the editable override. Both stored values survive a switch --
// only the toggle key changes. See README.md ("Monthly | Total toggle").
function MonthlyTotalRow({ label, toggleKey, monthlyField, totalField, rowTotal, scenario, onFieldChange, onToggleChange }) {
  const toggleValue = scenario.toggles?.[toggleKey]
  const isTotal = toggleValue === 'Total'
  return (
    <FieldRow
      label={label}
      control={
        <MonthlyTotalToggle
          value={toggleValue}
          onChange={(v) => onToggleChange(toggleKey, v)}
          ariaLabel={`${label} toggle`}
        />
      }
    >
      {/* Keys: without them React reuses one input instance across the switch and its typing draft
          shows up in the other box. */}
      {isTotal ? (
        <EditableValueBox money
          key="total"
          value={String(scenario[totalField] ?? '')}
          onChange={(v) => onFieldChange(totalField, v)}
          ariaLabel={`${label} Total`}
        />
      ) : (
        <>
          <EditableValueBox money
            key="monthly"
            value={String(scenario[monthlyField] ?? '')}
            onChange={(v) => onFieldChange(monthlyField, v)}
            width={72}
            ariaLabel={`${label} Monthly`}
          />
          {/* Spec 1.3 gaps are 8 before and 7 after the divider; FieldRow's own gap is 6. */}
          <span className="w-px h-[16px] ml-[2px] mr-[1px] bg-brand-fieldBdr shrink-0" aria-hidden="true" />
          <DerivedValueBox value={formatMoney(rowTotal)} width={88} ariaLabel={`${label} Total`} />
        </>
      )}
    </FieldRow>
  )
}

// scenario/onFieldChange/onToggleChange/computed: see README.md ("no save endpoint yet", "the
// computed prop contract"). No section number since SOW 4 -- README.md ("section numbering").
export default function HoldingCostsSection({ scenario, computed, onFieldChange, onToggleChange }) {
  const s = scenario ?? {}
  const isLoan = isLoanScenario(s.scenarioType)
  // Per-row values, allocation and gates are the backend's (reilm-be flip/holdingCosts.js,
  // calcHoldingCostRows); absent until a route serves computed, which renders the dash.
  const rows = computed?.holdingCostRows
  // The same gate calcHoldingCostRows uses: allocated to Selling Costs ("Paid at Sale"), the interest
  // is shown there and the row here goes (Cong, letter 99 item 8.2). Every other value is Monthly,
  // including a stored 'cashout' -- letter 104 (1.3) removed "Refinance Summ." and the backend puts
  // that interest here (reilm-be flip/holdingCosts.js, interest here unless 'sell').
  const accruedInterestHere = isLoan && s.sell_tax_applied !== 'sell'
  const rowProps = { scenario: s, onFieldChange, onToggleChange }

  return (
    <SectionCard title="Holding Costs" total={formatMoney(computed?.holding_cost)} firstRowGap={16}>
      <div className="flex flex-col gap-y-[8px]">
        {accruedInterestHere ? (
          <FieldRow label="Accrued Interest" tooltip={TOOLTIPS.accruedInterest}>
            {/* Hug width: the design's box is 42 wide around "6,910", not the usual 88 (spec D3). */}
            <DerivedValueBox
              value={formatMoney(rows?.accruedInterest)}
              hug
              ariaLabel="Accrued Interest (Holding Costs)"
            />
          </FieldRow>
        ) : null}
        {isLoan ? (
          <FieldRow label="Draw Fees" tooltip={TOOLTIPS.drawFees}>
            <EditableValueBox money
              value={String(s.financed_draw_fee ?? '')}
              onChange={(v) => onFieldChange('financed_draw_fee', v)}
              ariaLabel="Draw Fees"
            />
          </FieldRow>
        ) : null}
        <MonthlyTotalRow label="Utilities" toggleKey="holding_util" monthlyField="utilities0" totalField="utilities1" rowTotal={rows?.utilities} {...rowProps} />
        <MonthlyTotalRow label="HOA Fees" toggleKey="holding_hoa_fee" monthlyField="holding_hoa_fee1" totalField="holding_hoa_fee2" rowTotal={rows?.hoa} {...rowProps} />
      </div>
    </SectionCard>
  )
}
